import { useEffect, useState } from "react";
import { faFilter } from "@fortawesome/pro-regular-svg-icons";

import { parseNumber } from "common/utils/numbers";
import { useMatch } from "common/router/hooks";
import Spinner from "common/components/Spinner";
import Icon from "common/components/Icon";
import { FetchError } from "common/fetch/types";
import { Optional, WithStringValues } from "common/utils/types";
import { useListSearchState } from "common/hooks";

import Page from "client/common/components/layouts/Page";
import Container from "client/common/components/layouts/Container";
import Pagination from "client/common/components/layouts/Pagination";
import Results from "client/common/components/results/Results";
import LoadingPage from "client/common/components/loading/LoadingPage";
import Error404Page from "client/common/components/errors/Error404Page";
import { useClinic } from "client/clinics/hooks";
import Body from "client/common/components/layouts/Body";
import { Paths } from "client/routes";
import PillButton from "client/common/components/buttons/PillButton";
import { useBreakpoints } from "client/common/hooks";
import { sendViewItemList } from "client/analytics/utils";

import ProductLineCard from "./ProductLineCard";
import CategoryContentLookup from "./CategoryContent";
import {
  useProductLineSearchQuery,
  useCategoryQuery,
  useCategoryProductLineFilterOptionState,
} from "../hooks";
import { getCategoryProductLineFiltersFromSearch, getSearchString } from "../utils";
import BrowseSupport from "./BrowseSupport";
import PreFooter from "./PreFooter";
import CategoryProductLineSearchFilterTags from "./CategoryProductLineSearchFilterTags";
import ProductLineSearchFilterModal from "./CategoryProductLineSearchFilterModal";
import { ProductLineSearch, SelectedCategoryProductLineFilters } from "../types";

import styles from "../styles/CategoryPage.module.less";

const VIEW_NAME = "Category Page";

type CategorySearchUpdate = Partial<WithStringValues<Paths["products"]["category"]["Search"]>>;

export const initialProductLineFilters = {
  manufacturer_ids: [],
  species: [],
  prescription_required: [],
};

export const initialSearch = {
  ...initialProductLineFilters,
  page: 1,
};

type CategoryProductLinesProps = {
  id: number;
  productLines: Optional<ProductLineSearch>;
  isLoading: boolean;
  isRefetching: boolean;
  error: Optional<FetchError>;
  filters: SelectedCategoryProductLineFilters;
  onSearchChange: (search: CategorySearchUpdate) => void;
  onToggleFilterModal: () => void;
};

const CategoryProductLines = ({
  id,
  productLines,
  isLoading,
  isRefetching,
  error,
  filters,
  onSearchChange,
  onToggleFilterModal,
}: CategoryProductLinesProps) => {
  const { isPhoneAndSmaller } = useBreakpoints();
  const { data: filterState } = useCategoryProductLineFilterOptionState([id]);

  const renderContent = () => {
    if (isLoading) {
      return <Results.Loading description="Loading results" />;
    }

    if (!productLines || error) {
      return <Results.FetchError error={error} entityName="results" />;
    }

    if (!productLines.results.length) {
      return <Results.None header="No products found" />;
    }

    return (
      <div className={styles.search}>
        <div className={styles.products}>
          {productLines.results.map((productLine) => (
            <div key={productLine.id} className={styles.product}>
              {isPhoneAndSmaller ? (
                <ProductLineCard.Compact productLine={productLine} eventViewName={VIEW_NAME} />
              ) : (
                <ProductLineCard productLine={productLine} eventViewName={VIEW_NAME} />
              )}
            </div>
          ))}
        </div>
        <Pagination
          paginator={productLines.paginator}
          onPageChange={(page) => onSearchChange({ page })}
          entityName="result"
        />
      </div>
    );
  };

  const hasSomeFilters = Object.values(filterState?.filters ?? {}).some((filter) => filter.length);

  return (
    <div className={styles.content}>
      <div className={styles.info}>
        <div className={styles.label}>
          {isRefetching ? <Spinner /> : getSearchString(productLines?.paginator)}
        </div>
        {filterState && hasSomeFilters && (
          <div className={styles.actions}>
            <CategoryProductLineSearchFilterTags
              options={filterState.filters}
              value={filters}
              onChange={onSearchChange}
            />
            <PillButton variant="secondary" size="s" type="button" onClick={onToggleFilterModal}>
              <span className={styles.filter}>
                <Icon icon={faFilter} /> Filter
              </span>
            </PillButton>
          </div>
        )}
      </div>
      {renderContent()}
    </div>
  );
};

const CategoryPage = () => {
  const match = useMatch<Paths["products"]["category"]>();
  const clinic = useClinic();
  const parsedId = parseNumber(match.params.id, 0);

  const [search, setSearch] = useListSearchState<Paths["products"]["category"]>({
    page: 1,
    ...initialProductLineFilters,
  });

  const { data: filterState } = useCategoryProductLineFilterOptionState([parsedId]);

  const [isFiltering, setIsFiltering] = useState(false);

  const { data: category, isLoading: isCategoryLoading } = useCategoryQuery(parsedId);

  const filters = getCategoryProductLineFiltersFromSearch(search);

  const {
    data: productLines,
    error: productLineError,
    isRefetching: isRefetchingProductLines,
    isLoading: isLoadingProductLines,
  } = useProductLineSearchQuery({
    ...filters,
    category_ids: [parsedId],
    important_first: true,
    page_size: 20,
    q: "",
    sort: "",
    page: parseNumber(search.page, 1),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [search.page]);

  // Send item list to GA4
  useEffect(() => {
    if (productLines) {
      sendViewItemList(productLines.results, VIEW_NAME);
    }
  }, [productLines]);

  if (isCategoryLoading) {
    return <LoadingPage />;
  }

  if (!category) {
    return <Error404Page />;
  }

  const CategoryContent = CategoryContentLookup[category.slug] ?? CategoryContentLookup.default;

  return (
    <Page
      style={{
        background: `radial-gradient(farthest-corner at 30% 20%, ${CategoryContent.gradient.start} 0%, ${CategoryContent.gradient.stop} 50%)`,
      }}
      className={styles.page}
    >
      <div>
        <Container>
          <div className={styles.top}>
            <div className={styles.heading}>
              <h1 className={styles.title}>{category.extended_name}</h1>
              <div className={styles.description}>{category.description}</div>
            </div>
            <CategoryContent.Icon className={styles.icon} />
          </div>
        </Container>
        <div className={styles.main}>
          <Body>
            <CategoryProductLines
              id={parsedId}
              productLines={productLines}
              filters={filters}
              isLoading={isLoadingProductLines}
              isRefetching={isRefetchingProductLines}
              error={productLineError}
              onSearchChange={setSearch}
              onToggleFilterModal={() => setIsFiltering(!isFiltering)}
            />
            {filterState && (
              <ProductLineSearchFilterModal
                options={filterState.filters}
                isVisible={isFiltering}
                onClose={() => setIsFiltering(false)}
                value={filters}
                onChange={setSearch}
              />
            )}
          </Body>
        </div>
        <div className={styles.support}>
          <Container>
            <BrowseSupport />
          </Container>
        </div>
      </div>
      <PreFooter className={styles.preFooter} image={clinic.storefront_prefooter_image} />
    </Page>
  );
};

export default CategoryPage;
